import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isImageFileUrl } from 'common/util/imageExtensions';
import log from 'client/lib/log';
import {
  isPDFFile,
  createFileObjToBeUploaded,
  uploadFileToS3,
  makeProxiedS3FilePath,
  getFileValidationError,
} from 'client/lib/fileUploader';

import ButtonDeprecated, {
  BUTTON_THEME_BLUE_GREYCLIFF,
  BUTTON_THEME_GRAY_GREYCLIFF,
} from 'client/components/ButtonDeprecated';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import SrCheckbox from 'client/components/SrCheckbox';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';

class SrFileUploader extends Component {
  static propTypes = {
    value: PropTypes.string,
    accept: PropTypes.string,
    isPrivate: PropTypes.bool,
    uploadAs: PropTypes.number,
    minWidth: PropTypes.number,
    minHeight: PropTypes.number,
    filePath: PropTypes.string.isRequired,
    onFileUploaded: PropTypes.func.isRequired,
    onFileRemoved: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    warning: PropTypes.node,
    checkmarks: PropTypes.array,
    successMessage: PropTypes.string,
    downloadButtonLabel: PropTypes.string,
    requirePDFDocs: PropTypes.bool,
    conditionalSuccessMessage: PropTypes.bool,
    // Optional override for how the selected file is uploaded. Defaults to
    // `uploadFileToS3` (web's /upload-file → AWS_BUCKET). Pass a custom fn — e.g.
    // one backed by `uploadFileToUserBucket` — to land the file in a different
    // bucket. Must resolve to `{ s3FileUrl }`.
    uploadFn: PropTypes.func,
  };

  static defaultProps = {
    accept: '*',
    placeholder: 'tap to upload',
    isPrivate: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      s3FileUrl: props.value || null,
      uploading: false,
      isDragActive: false,
      checks:
        props.value && props.checkmarks
          ? props.checkmarks.reduce((memo, check) => ({ ...memo, [check]: true }), {})
          : {},
    };
    this._onFileInputChange = this._onFileInputChange.bind(this);
    this._isUnmounting = false;
    this._uploadInputEl = null;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.value && nextProps.value && this.props.value !== nextProps.value) {
      this.setState({
        s3FileUrl: nextProps.value || null,
        uploading: false,
        checks:
          nextProps.value && nextProps.checkmarks
            ? nextProps.checkmarks.reduce((memo, check) => ({ ...memo, [check]: true }), {})
            : {},
      });
    } else if (
      !this.props.value &&
      nextProps.value &&
      !this.state.uploading &&
      this.state.s3FileUrl !== nextProps.value
    ) {
      this.setState({
        s3FileUrl: nextProps.value,
      });
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onUploadInputRef = (el) => {
    this._uploadInputEl = el;
  };

  _onFileInputChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    this._onFile(file);
  };

  _onDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      this.setState({ isDragActive: true });
    } else if (event.type === 'dragleave') {
      this.setState({ isDragActive: false });
    }
  };

  _onDropFile = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (!file) {
      return;
    }
    this.setState({ isDragActive: false });
    this._onFile(file);
  };

  async _onFile(file) {
    try {
      const { filePath, isPrivate, uploadAs, minWidth, minHeight, uploadFn } = this.props;
      const fileObjectToBeUploaded = createFileObjToBeUploaded(file);

      const validationError = await getFileValidationError(fileObjectToBeUploaded, {
        minWidth,
        minHeight,
      });
      if (validationError) {
        this.setState(
          {
            s3FileUrl: null,
            uploading: false,
            validationError,
          },
          this._validateUpload,
        );
        return;
      }

      this.setState({
        s3FileUrl: null,
        uploading: true,
        validationError,
      });

      const uploadResponse = await (uploadFn || uploadFileToS3)(fileObjectToBeUploaded, {
        filePath,
        isPrivate,
        uploadAs,
      });
      if (this._isUnmounting) {
        return;
      }
      this.setState(
        {
          s3FileUrl: uploadResponse.s3FileUrl,
          uploading: false,
          validationError,
        },
        this._validateUpload,
      );
    } catch (error) {
      log.error(error);
    }
  }

  _validateUpload = () => {
    const { checkmarks, onFileUploaded } = this.props;
    const { s3FileUrl, checks, validationError } = this.state;

    // If has any validation error
    if (validationError) {
      onFileUploaded({ s3FileUrl: null });
      return;
    }

    // Update file url if there are no checkmarks
    if (!checkmarks) {
      onFileUploaded({ s3FileUrl });
      return;
    }

    // Check if all the checkmarks are checked
    const allCheckmarksChecked = checkmarks.every((checkmark) => checks[checkmark]);
    onFileUploaded({ s3FileUrl: allCheckmarksChecked ? s3FileUrl : null });
  };

  _onClickCheckbox = (checkmark) => {
    const checks = {
      ...this.state.checks,
      [checkmark]: !this.state.checks[checkmark],
    };

    this.setState({ checks }, this._validateUpload);
  };

  _onReUploadButtonClick = () => {
    const { onFileRemoved } = this.props;
    this.setState({
      s3FileUrl: null,
      uploading: false,
    });

    if (this._uploadInputEl) {
      this._uploadInputEl.click();
    }
    onFileRemoved();
  };

  render() {
    const {
      accept,
      placeholder,
      warning,
      checkmarks,
      successMessage,
      successMessageTextClassName,
      downloadButtonLabel,
      isPrivate,
      requirePDFDocs,
      className,
      conditionalSuccessMessage,
    } = this.props;
    const { s3FileUrl, uploading, isDragActive, checks, validationError } = this.state;
    const isPreviewableFile = isImageFileUrl(s3FileUrl) || isPDFFile(s3FileUrl);

    return (
      <div
        className={className}
        onClick={(e) => e.stopPropagation()}
        onDrop={this._onDropFile}
        onDragEnter={this._onDrag}
        onDragOver={(e) => e.preventDefault()}
      >
        {/* Upload prompt when no file and not uploading */}
        {!uploading && !isDragActive && !s3FileUrl && (
          <label htmlFor="file-upload-input" className="fileUpload upload">
            <span className="text">{placeholder}</span>
            <input
              id="file-upload-input"
              ref={this._onUploadInputRef}
              type="file"
              accept={accept}
              onChange={this._onFileInputChange}
            />
          </label>
        )}
        {/* Drag drop indicator */}
        {isDragActive && (
          <div
            className="fileUpload fileUploadDragDrop"
            onDragEnter={this._handleDrag}
            onDragLeave={this._handleDrag}
            onDragOver={this._handleDrag}
          >
            <span className="text">Drop your file!</span>
          </div>
        )}
        {/* Uploading indicator */}
        {uploading && (
          <span className="fileUpload upload">
            <span className="text">uploading...</span>
          </span>
        )}
        {/* File uploaded - display preview and/or buttons */}
        {!uploading && !isDragActive && s3FileUrl && (
          <>
            {/* Success message for non-previewable files (if enabled) */}
            {(successMessage || conditionalSuccessMessage) && !isPreviewableFile && (
              <span className="fileUpload successMessage" onClick={this._onReUploadButtonClick}>
                <span className={classnames('text', successMessageTextClassName)}>
                  {successMessage || 'File uploaded successfully'}
                </span>
                <span className="subtext">Tap here to upload a different file</span>
              </span>
            )}
            {/* Preview for image and PDF files */}
            {isPreviewableFile && (
              <span className="fileUpload success">
                {isImageFileUrl(s3FileUrl) ? (
                  <div
                    className="uploadThumbnail"
                    style={{
                      backgroundImage: `url("${
                        isPrivate ? makeProxiedS3FilePath(s3FileUrl) : s3FileUrl
                      }")`,
                    }}
                  />
                ) : (
                  <SrS3FileDisplay url={s3FileUrl} />
                )}
              </span>
            )}
            {/* Download and upload buttons */}
            <SrBox marginTop="14px" marginBottom="14px">
              <ButtonDeprecated theme={BUTTON_THEME_BLUE_GREYCLIFF} href={s3FileUrl} download>
                {downloadButtonLabel || 'Download file'}
              </ButtonDeprecated>
              <SrBox marginTop="14px">
                <ButtonDeprecated theme={BUTTON_THEME_GRAY_GREYCLIFF} onClick={this._onReUploadButtonClick}>
                  <span className="subtext">Upload a different file</span>
                </ButtonDeprecated>
              </SrBox>
            </SrBox>
          </>
        )}
        {/* PDF requirement warning */}
        {s3FileUrl && requirePDFDocs && !isPDFFile(s3FileUrl) && (
          <SrBox padding="0px 19px 10px">
            <SrText color="red" weight="bold">
              The document provided is not a PDF. Please upload a PDF instead, if possible.
            </SrText>
          </SrBox>
        )}
        {/* Validation error message */}
        {validationError && (
          <SrBox padding="0px 19px">
            <SrText color="red" weight="bold">
              {validationError}
            </SrText>
          </SrBox>
        )}
        {/* Warning and checkmark section */}
        {warning && <div className="fileUploadWarningChecklistHeader">{warning}</div>}
        {Array.isArray(checkmarks) &&
          checkmarks.map((checkmark) => (
            <div className="fileUploadWarningCheckmark" key={checkmark}>
              <SrCheckbox
                onClick={() => this._onClickCheckbox(checkmark)}
                checked={checks[checkmark]}
              >
                {checkmark}
              </SrCheckbox>
            </div>
          ))}
      </div>
    );
  }
}

SrFileUploader.displayName = 'SrFileUploader';

export default SrFileUploader;
